import React, { useEffect, useState } from 'react';
import { apiService } from '../services/api';
import Modal from './Modal';

interface EmailTemplate {
  id: string;
  name: string;
  display_name: string;
  language: string;
  type: string;
  file_path: string;
  description: string;
}

interface ImportDialogProps {
  onImport: (file: File, overwriteExisting: boolean) => void;
  onCancel: () => void;
}

const ImportDialog: React.FC<ImportDialogProps> = ({ onImport, onCancel }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [overwriteExisting, setOverwriteExisting] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      alert('Please select a file to import');
      return;
    }
    onImport(selectedFile, overwriteExisting);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block mb-1 font-medium">Select File *</label>
        <input
          type="file"
          accept=".html,.zip"
          onChange={handleFileChange}
          className="w-full border px-3 py-2 rounded"
          required
        />
        {selectedFile && (
          <p className="text-sm text-gray-600 mt-1">Selected: {selectedFile.name}</p>
        )}
        <p className="text-sm text-gray-600 mt-1">
          Supported formats: HTML files (.html) or ZIP archives containing HTML files
        </p>
      </div>

      <div>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={overwriteExisting}
            onChange={(e) => setOverwriteExisting(e.target.checked)}
          />
          <span>Overwrite existing templates</span>
        </label>
        <p className="text-sm text-gray-600 mt-1">
          If checked, existing templates with the same filename will be replaced
        </p>
      </div>

      <div className="text-right space-x-2">
        <button type="button" className="btn-secondary px-4 py-2" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn-primary px-4 py-2">
          Import
        </button>
      </div>
    </form>
  );
};

const EmailTemplateManager: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showImportDialog, setShowImportDialog] = useState(false);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.axiosInstance.get('/api/v1/emails/templates');
      setTemplates(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch email templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleImport = async (file: File, overwriteExisting: boolean) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('overwrite_existing', overwriteExisting.toString());

      const response = await apiService.axiosInstance.post('/api/v1/emails/templates/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Show detailed results
      const result = response.data;
      let message = result.message;
      
      if (result.errors && result.errors.length > 0) {
        message += '\n\nErrors:\n' + result.errors.join('\n');
      }
      
      alert(message);
      
      await fetchTemplates();
      setShowImportDialog(false);
    } catch (err: any) {
      alert(err.response?.data?.detail || err.message || 'Import failed');
    }
  };

  const handleExport = async () => {
    try {
      const response = await apiService.axiosInstance.post('/api/v1/emails/templates/export?include_content=true');
      const payload = response.data || {};
      const templates = Array.isArray(payload.templates) ? payload.templates : [];
      const exportData = {
        generated_at: payload.generated_at,
        count: templates.length,
        templates,
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `email_templates_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      alert(payload.message || `Exported ${templates.length} template(s).`);
    } catch (err: any) {
      alert(err.response?.data?.detail || err.message || 'Export failed');
    }
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      'invoice_notification': 'bg-blue-100 text-blue-800',
      'reminder': 'bg-yellow-100 text-yellow-800',
      'payment': 'bg-green-100 text-green-800',
      'base': 'bg-gray-100 text-gray-800',
      'other': 'bg-purple-100 text-purple-800'
    };
    return colors[type] || colors['other'];
  };

  const getLanguageFlag = (language: string) => {
    const flags: Record<string, string> = {
      'en': '🇺🇸',
      'de': '🇩🇪',
      'pt-br': '🇧🇷'
    };
    return flags[language] || '🌐';
  };

  return (
    <div className="p-6 bg-white rounded shadow">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Email Templates</h2>
        <div className="space-x-2">
          <button className="btn-secondary px-4 py-2" onClick={() => setShowImportDialog(true)}>
            Import Templates
          </button>
          <button className="btn-secondary px-4 py-2" onClick={handleExport}>
            Export Templates
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2">Loading templates...</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="border px-3 py-2 text-left">Template Name</th>
                <th className="border px-3 py-2 text-left">Type</th>
                <th className="border px-3 py-2 text-center">Language</th>
                <th className="border px-3 py-2 text-left">Description</th>
                <th className="border px-3 py-2 text-left">File</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((template) => (
                <tr key={template.id} className="hover:bg-gray-50">
                  <td className="border px-3 py-2 font-medium">{template.display_name}</td>
                  <td className="border px-3 py-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(template.type)}`}>
                      {template.type.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="border px-3 py-2 text-center">
                    <span className="text-lg" title={template.language}>
                      {getLanguageFlag(template.language)}
                    </span>
                    <span className="ml-1 text-xs text-gray-600">
                      {template.language.toUpperCase()}
                    </span>
                  </td>
                  <td className="border px-3 py-2 text-gray-600">{template.description}</td>
                  <td className="border px-3 py-2 font-mono text-xs text-gray-500">{template.file_path}</td>
                </tr>
              ))}
              {templates.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-gray-500">
                    No email templates found. Use the Import button to add templates.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showImportDialog && (
        <Modal title="Import Email Templates" onClose={() => setShowImportDialog(false)}>
          <ImportDialog
            onImport={handleImport}
            onCancel={() => setShowImportDialog(false)}
          />
        </Modal>
      )}

      <style>{`
        .btn-primary {
          background-color: #3b82f6;
          color: white;
          border: none;
          border-radius: 0.375rem;
          padding: 0.5rem 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .btn-primary:hover {
          background-color: #2563eb;
        }
        
        .btn-secondary {
          background-color: #f3f4f6;
          color: #374151;
          border: 1px solid #d1d5db;
          border-radius: 0.375rem;
          padding: 0.5rem 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .btn-secondary:hover {
          background-color: #e5e7eb;
        }
      `}</style>
    </div>
  );
};

export default EmailTemplateManager;
